import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import { Box, Button, CircularProgress, Divider, IconButton, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography, Menu, MenuItem, ListItemIcon, FormControl, InputLabel, Select } from "@mui/material";
import { useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useSnackbar } from "../../../core/contexts/SnackbarContext";
import { useCupons } from "../../hooks/useCupons";
import { useFetchCupons } from "../../hooks/useFetchCupons";
import { ICupom } from "../../../pedidos/interface/ICupom";
import { CadastrarEditarCupomModal } from "./containers/CadastrarEditarCupomModal";
import { ExcluirCupomModal } from "./containers/ExcluirCupomModal";
import { ButtonsContainer, FiltrosContainer, Loading, NenhumCupom, PageContainer, TableContainer } from "./styles";
import { useAutenticacaoContext } from '../../../core/contexts/AutenticacaoContext';
import { useNavigate } from 'react-router-dom';
import { CupomTableRow } from '../../components/CupomTableRow';

export const ListarCupons = () => {
    const [cupomEdicao, setCupomEdicao] = useState<ICupom | undefined>(undefined);
    const [cupomExclusao, setCupomExclusao] = useState<ICupom | undefined>(undefined);

    // Estados para seleção múltipla
    const [cuponsSelecionados, setCuponsSelecionados] = useState<string[]>([]);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const openAcoes = Boolean(anchorEl);

    const snackbar = useSnackbar();

    const [openCadastrarEditar, setOpenCadastrarEditar] = useState(false);
    const handleOpenCadastrarEditar = () => setOpenCadastrarEditar(true);
    const handleCloseCadastrarEditar = () => setOpenCadastrarEditar(false);

    const [openExcluir, setOpenExcluir] = useState(false);
    const handleOpenExcluir = () => setOpenExcluir(true);
    const handleCloseExcluir = () => setOpenExcluir(false);

    const { criar, atualizar, loading, deletar, deletarEmLote } = useCupons();
    const {
        dados,
        handleRefresh,
        handleLimpar,
        handleCodigoChange,
        handleNomeChange,
        handleAtivoChange,
        isLoading,
        codigo,
        nome,
        ativo,
        handleFiltrar
    } = useFetchCupons();

    // Funções para seleção múltipla
    const toggleCupomSelecionado = (uuid: string) => {
        setCuponsSelecionados(prev =>
            prev.includes(uuid) ? prev.filter(id => id !== uuid) : [...prev, uuid]
        );
    };

    const limparSelecao = () => {
        setCuponsSelecionados([]);
    };

    const abrirAcoes = (event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget);
    const fecharAcoes = () => setAnchorEl(null);

    // Funções para ações em lote
    const handleEditarCupomSelecionado = () => {
        if (cuponsSelecionados.length === 1) {
            const cupom = Array.isArray(dados) ? dados.find((c: ICupom) => c.uuid === cuponsSelecionados[0]) : undefined;
            if (cupom) {
                setCupomEdicao(cupom);
                setOpenCadastrarEditar(true);
                limparSelecao();
            }
        }
        fecharAcoes();
    };

    const handleExcluirCuponsSelecionados = async () => {
        if (cuponsSelecionados.length > 0) {
            const resultado = await deletarEmLote(cuponsSelecionados);
            if (resultado) {
                handleRefresh();
                limparSelecao();
            }
        }
        fecharAcoes();
    };

    const handleCancelarEditarCadastrar = () => {
        handleCloseCadastrarEditar();
        setCupomEdicao(undefined);
    }

    const handleCadastrarCupom = async (cupomData: FieldValues) => {
        console.log("handleCadastrarCupom chamado com:", cupomData);
        
        if (loading) {
            console.log("Loading está true, retornando");
            return;
        }

        const marketplacesFormatados = cupomData.marketplaces?.map((m: { uuid?: string; tipo: string }) => {
            if (m.tipo === "PDV") {
                return { tipo: m.tipo, uuid: null };
            }
            return { tipo: m.tipo, uuid: m.uuid };
        });

        let dataValidade: Date | undefined = undefined;
        if (cupomData.dataValidade !== null && cupomData.dataValidade !== undefined && cupomData.dataValidade !== "") {
            const dataStr = typeof cupomData.dataValidade === 'string' 
                ? cupomData.dataValidade.trim() 
                : String(cupomData.dataValidade).trim();
            if (dataStr !== "" && dataStr !== "null" && dataStr !== "undefined") {
                try {
                    const data = new Date(dataStr);
                    if (!isNaN(data.getTime())) {
                        dataValidade = data;
                    }
                } catch (error) {
                }
            }
        }

        // Construir o objeto sem incluir dataValidade se não houver valor
        const { dataValidade: _, ...cupomDataSemDataValidade } = cupomData;
        
        const cupom: ICupom = {
            ...cupomDataSemDataValidade,
            valor: Number(cupomData.valor),
            limiteUsoPorUsuario: cupomData.limiteUsoPorUsuario ? Number(cupomData.limiteUsoPorUsuario) : null,
            marketplaces: marketplacesFormatados
        } as ICupom;
        
        // Só incluir dataValidade se houver valor
        if (dataValidade !== undefined) {
            cupom.dataValidade = dataValidade;
        }

        const cupomCadastrado = await criar(cupom);

        if (!cupomCadastrado) {
            return snackbar("Ocorreu um erro ao cadastrar o cupom", { severity: "error" })
        }

        handleRefresh();
        handleCancelarEditarCadastrar();
        return snackbar("Cupom cadastrado com sucesso", { severity: "success" })
    }

    const handleEditarCupom = async (cupomData: FieldValues) => {
        if (loading) {
            return;
        }

        const marketplacesFormatados = cupomData.marketplaces?.map((m: { uuid?: string; tipo: string }) => {
            if (m.tipo === "PDV") {
                return { tipo: m.tipo, uuid: null };
            }
            return { tipo: m.tipo, uuid: m.uuid };
        });

        let dataValidade: Date | undefined = undefined;
        if (cupomData.dataValidade !== null && cupomData.dataValidade !== undefined && cupomData.dataValidade !== "") {
            const dataStr = typeof cupomData.dataValidade === 'string' 
                ? cupomData.dataValidade.trim() 
                : String(cupomData.dataValidade).trim();
            if (dataStr !== "" && dataStr !== "null" && dataStr !== "undefined") {
                try {
                    const data = new Date(dataStr);
                    if (!isNaN(data.getTime())) {
                        dataValidade = data;
                    }
                } catch (error) {
                }
            }
        }

        // Construir o objeto sem incluir dataValidade se não houver valor
        const { dataValidade: _, ...cupomDataSemDataValidade } = cupomData;
        
        const dados: ICupom = {
            ...cupomDataSemDataValidade,
            valor: Number(cupomData.valor),
            limiteUsoPorUsuario: cupomData.limiteUsoPorUsuario ? Number(cupomData.limiteUsoPorUsuario) : null,
            uuid: cupomEdicao?.uuid,
            marketplaces: marketplacesFormatados
        } as ICupom;
        
        // Só incluir dataValidade se houver valor
        if (dataValidade !== undefined) {
            dados.dataValidade = dataValidade;
        }

        const cupomEditado = await atualizar(dados.uuid!, dados);
        if (!cupomEditado) {
            return snackbar("Ocorreu um erro ao editar o cupom", { severity: "error" })
        }

        handleRefresh();
        handleCancelarEditarCadastrar();
        return snackbar("Cupom editado com sucesso", { severity: "success" })
    }

    const handleExcluirCupom = async (cupomUuid: string) => {
        if (loading) {
            return;
        }

        const cupomExcluido = await deletar(cupomUuid);
        if (!cupomExcluido) {
            return snackbar("Ocorreu um erro ao excluir o cupom", { severity: "error" })
        }

        handleRefresh();
        handleCancelarExcluir();
        return snackbar("Cupom excluído com sucesso", { severity: "success" })
    }

    const handleAbrirEdicaoCupom = (cupom: ICupom) => {
        setCupomEdicao(cupom);
        setOpenCadastrarEditar(true);
    }

    const handleAbrirExclusaoCupom = (cupom: ICupom) => {
        setCupomExclusao(cupom);
        handleOpenExcluir();
    }

    const handleCancelarExcluir = () => {
        setCupomExclusao(undefined);
        handleCloseExcluir();
    }

    const navigate = useNavigate();
    const { verificarAcessoModulo, perfil } = useAutenticacaoContext();
    useEffect(() => {
        if(perfil && !verificarAcessoModulo("Cardápio")) {
            navigate("/nao-autorizado")
        }
    }, [perfil])
    
    const cupons = Array.isArray(dados) ? dados : [];
    
    return (
        <PageContainer>
            <CadastrarEditarCupomModal
                open={openCadastrarEditar}
                handleCadastrarCupom={handleCadastrarCupom}
                handleEditarCupom={handleEditarCupom}
                handleCancelar={handleCancelarEditarCadastrar}
                loading={loading}
                dadosCupom={cupomEdicao}
            />
            <ExcluirCupomModal
                cupom={cupomExclusao}
                open={openExcluir}
                excluir={handleExcluirCupom}
                cancelar={handleCancelarExcluir}
            />
            <FiltrosContainer>
                <Typography variant='h6'>Cupons</Typography>
                <Box className="filtros-cupons">
                    <TextField
                        placeholder="Código"
                        type="text"
                        value={codigo || ''}
                        size='small'
                        onChange={handleCodigoChange}
                        className='input-cupom'
                    />
                    <TextField
                        placeholder="Nome"
                        type="text"
                        value={nome || ''}
                        size='small'
                        onChange={handleNomeChange}
                        className='input-cupom'
                    />
                    <FormControl size="small" className='input-cupom'>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={ativo === undefined ? '' : ativo ? 'ativo' : 'inativo'}
                            label="Status"
                            onChange={(e) => {
                                const value = e.target.value;
                                handleAtivoChange(value === '' ? undefined : value === 'ativo');
                            }}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            <MenuItem value="ativo">Ativo</MenuItem>
                            <MenuItem value="inativo">Inativo</MenuItem>
                        </Select>
                    </FormControl>
                    <ButtonsContainer>
                        <Button
                            variant="outlined"
                            onClick={handleLimpar}
                        >
                            Limpar
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleFiltrar}
                        >
                            Filtrar
                        </Button>
                        <Divider orientation="vertical" flexItem sx={{ mr: "0.5rem", ml: "0.5rem" }} />
                        <Button
                            disabled={cuponsSelecionados.length <= 0}
                            variant="contained"
                            endIcon={
                                <ArrowDropDown sx={{ transform: openAcoes ? 'rotate(-180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }} />
                            }
                            onClick={abrirAcoes}
                        >
                            Ações
                        </Button>
                        <Menu
                            anchorEl={anchorEl}
                            open={openAcoes}
                            onClose={fecharAcoes}
                        >
                            <MenuItem disabled>
                                <Typography variant="inherit" noWrap>
                                    {cuponsSelecionados.length} cupom{cuponsSelecionados.length !== 1 ? 's' : ''} selecionado{cuponsSelecionados.length !== 1 ? 's' : ''}
                                </Typography>
                            </MenuItem>
                            <Divider />
                            <MenuItem 
                                onClick={handleEditarCupomSelecionado}
                                disabled={cuponsSelecionados.length !== 1}
                            >
                                <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                                <Typography variant="inherit">Editar</Typography>
                            </MenuItem>
                            <MenuItem onClick={handleExcluirCuponsSelecionados}>
                                <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
                                <Typography variant="inherit">Excluir</Typography>
                            </MenuItem>
                        </Menu>
                        <Divider orientation="vertical" flexItem sx={{ mr: "0.5rem", ml: "0.5rem" }} />
                        <Tooltip title="Cadastre um novo cupom">
                            <Button
                                onClick={handleOpenCadastrarEditar}
                                variant="contained"
                                startIcon={<AddIcon />}
                            >
                                Novo
                            </Button>
                        </Tooltip>
                    </ButtonsContainer>
                </Box>
            </FiltrosContainer>
            <Paper sx={{
                padding: "1rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                margin: "0 auto",
                width: "85%"
            }}>

                <TableContainer>
                    {isLoading
                        ? (
                            <Loading>
                                <CircularProgress />
                            </Loading>
                        )
                        : cupons && cupons.length ? (
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                        </TableCell>
                                        <TableCell align="left" width="15%">Código</TableCell>
                                        <TableCell align="left" width="20%">Nome</TableCell>
                                        <TableCell align="left" width="15%">Tipo</TableCell>
                                        <TableCell align="left" width="15%">Valor</TableCell>
                                        <TableCell align="center" width="10%">Limite por usuário</TableCell>
                                        <TableCell align="center" width="10%">Usos</TableCell>
                                        <TableCell align="center" width="10%">Clientes</TableCell>
                                        <TableCell align="left" width="15%">Data Validade</TableCell>
                                        <TableCell align="center" width="10%">Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {cupons.map((cupom: ICupom) => (
                                        <CupomTableRow
                                            key={cupom.uuid || 'temp-key'}
                                            cupom={cupom}
                                            selecionado={cupom.uuid ? cuponsSelecionados.includes(cupom.uuid) : false}
                                            toggleSelecionado={toggleCupomSelecionado}
                                            onEditar={handleAbrirEdicaoCupom}
                                            onExcluir={handleAbrirExclusaoCupom}
                                        />
                                    ))}
                                </TableBody>
                            </Table>
                        ) : (
                            <NenhumCupom>
                                <Typography>
                                    Nenhum cupom cadastrado
                                </Typography>
                            </NenhumCupom>
                        )}
                </TableContainer>
            </Paper>
        </PageContainer>
    );
}
